import React, { useEffect, useRef, useState, ReactNode } from "react";
import { createPortal } from "react-dom";
import { IframeDocumentProvider } from "../IframeDocumentContext"; // Import the Context Provider

interface IFrameWrapperProps {
  children: ReactNode;
  className?: string;
  iframeStyle?: React.CSSProperties;
  onContainerReady?: (ref: any) => void;
}

const IFrameWrapper: React.FC<IFrameWrapperProps> = ({
  children,
  className,
  iframeStyle,
  onContainerReady,
}) => {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const [iframeContentContainer, setIframeContentContainer] = useState<HTMLElement | null>(null);
  const [iframeDocument, setIframeDocument] = useState<Document | null>(null);
  
  const isInitialized = useRef(false);

  useEffect(() => {
    if (isInitialized.current) {
      return;
    }

    isInitialized.current = true;

    const onIframeReady = (): Promise<Document> => {
      return new Promise((resolve, reject) => {
        const iframeElement = iframeRef.current;
        if (!iframeElement) {
          reject(new Error("Iframe ref is not available."));
          return;
        }

        const iframeWindow = iframeElement.contentWindow;
        if (!iframeWindow) {
          reject(new Error("Iframe contentWindow is not available."));
          return;
        }

        const iframeDocument = iframeWindow.document;
        if (iframeDocument.readyState === "complete") {
          resolve(iframeDocument);
        } else {
          const handleLoad = () => {
            resolve(iframeWindow.document);
          };

          iframeElement.addEventListener("load", handleLoad);

          const timeoutId = setTimeout(() => {
            iframeElement.removeEventListener("load", handleLoad);
            reject(new Error("Iframe load timed out."));
          }, 10000);

          const cleanup = () => {
            clearTimeout(timeoutId);
            iframeElement.removeEventListener("load", handleLoad);
          };

          iframeElement.addEventListener("load", cleanup);
        }
      });
    };

    onIframeReady()
      .then((iframeDoc) => {
        const tailwindLink = iframeDoc.createElement("link");
        tailwindLink.rel = "stylesheet";
        tailwindLink.href = `https://s3.enrich.so/widget/style.css?ts=${new Date().getTime()}`;
        iframeDoc.head.appendChild(tailwindLink);

        let container = iframeDoc.querySelector("main");
        if (!container) {
          container = iframeDoc.createElement("main");
          iframeDoc.body.appendChild(container);
        }
        setIframeContentContainer(container);
        setIframeDocument(iframeDoc);
        onContainerReady?.(iframeRef);
      })
      .catch((error) => {
        console.error("Error initializing iframe:", error);
      });
  }, [onContainerReady]);

  return (
    <iframe
      className={className}
      ref={iframeRef}
      style={iframeStyle}
      frameBorder={0}
      allowFullScreen={true}
      allowTransparency={true}
      allow="autoplay;midi;geolocation;camera;microphone;encrypted-media;"
    >
      {iframeContentContainer && iframeDocument
        ? createPortal(
            <IframeDocumentProvider value={{iframeDocument, class_name: className}}>
              {children}
            </IframeDocumentProvider>,
            iframeContentContainer
          )
        : null}
    </iframe>
  );
};

export default IFrameWrapper;
